import { BUILD, wallHitbox } from "@common/definitions/building";
import { ObjectCategory, ZIndexes } from "@common/constants";
import { MaterialSounds, type ObstacleDefinition } from "@common/definitions/obstacles";
import { type Orientation, type Variation } from "@common/typings";
import { HitboxType, RectangleHitbox, type Hitbox } from "@common/utils/hitbox";
import { equivLayer } from "@common/utils/layer";
import { Angle, calculateDoorHitboxes, EaseFunctions, Numeric } from "@common/utils/math";
import { type Timeout } from "@common/utils/misc";
import { type ObjectsNetData } from "@common/utils/objectsSerializations";
import { random, randomBoolean, randomFloat, randomRotation } from "@common/utils/random";
import { Vec, type Vector } from "@common/utils/vector";
import { Graphics, GraphicsContext } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { CombatFx, particleTint } from "../managers/combatFx";
import { CombatSounds } from "../managers/combatSounds";
import { impactKindOf } from "../managers/combatSoundsLogic";
import { DamageMarks } from "../managers/damageMarks";
import { NightLight } from "../managers/nightLight";
import { ParticleManager, type Particle, type ParticleEmitter, type ParticleOptions } from "../managers/particleManager";
import { SnowLook, underRoof } from "../managers/snowLook";
import { SoundManager, type GameSound } from "../managers/soundManager";
import { DIFF_LAYER_HITBOX_OPACITY, HITBOX_COLORS, PIXI_SCALE, WALL_STROKE_WIDTH } from "../utils/constants";
import { DebugRenderer } from "../utils/debugRenderer";
import { drawGroundGraphics, TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { type Tween } from "../utils/tween";
import { GameObject } from "./gameObject";
import { type Player } from "./player";
import { GravityFx } from "../gravity/gravityFx";

/**
 * each kind of wall's picture (its border and its face, by their colours and its size), drawn once and shared by the
 * walls like it (0.98.8); never cleared or drawn on: a wall's own Graphics only shows it
 */
const wallContexts = new Map<string, GraphicsContext>();

/** a bullet's strike on an obstacle or a wall: which way it flew, how hard it hits, and whether it glanced off */
export interface BulletStrike {
    readonly direction: Vector
    readonly damage: number
    /** the surface turned it (steel that reflects bullets): it whines away */
    readonly glanced: boolean
}

export class Obstacle extends GameObject.derive(ObjectCategory.Obstacle) {
    override readonly damageable = true;

    readonly image: TrenchioSprite;
    smokeEmitter?: ParticleEmitter;
    particleEmitter?: ParticleEmitter;
    particleFrames!: string[];

    definition!: ObstacleDefinition;
    scale!: number;
    variation?: Variation;

    animationFrame?: number;

    /**
     * `undefined` if this obstacle hasn't been updated yet, or if it's not a door obstacle
     */
    private _door?: {
        closedHitbox?: RectangleHitbox
        openHitbox?: RectangleHitbox
        openAltHitbox?: RectangleHitbox
        hitbox?: RectangleHitbox
        offset: number
        locked?: boolean
    };

    get door(): {
        readonly closedHitbox?: RectangleHitbox
        readonly openHitbox?: RectangleHitbox
        readonly openAltHitbox?: RectangleHitbox
        readonly hitbox?: RectangleHitbox
        readonly offset: number
        readonly locked?: boolean
    } | undefined { return this._door; }

    get isDoor(): boolean { return this._door !== undefined; }

    activated?: boolean;
    hitbox!: Hitbox;
    orientation: Orientation = 0;

    leavesSprite?: TrenchioSprite;
    /** the crown's picture (a tree), for its cap of snow (0.98.5) */
    private _leavesFrame?: string;
    /** under a building's roof (0.98.5): no snow lies on it */
    private _indoors?: boolean;

    mountSprite?: TrenchioSprite;

    waterOverlaySprite?: TrenchioSprite;
    waterOverlay = false;

    powered = false;

    hitSound?: GameSound;

    notOnCoolDown = true;

    doorMask?: Graphics;

    graphics?: Graphics;

    private _glowTween?: Tween<Particle>;
    private _flickerTimeout?: Timeout;
    private _glow?: Particle;

    constructor(id: number, data: ObjectsNetData[ObjectCategory.Obstacle]) {
        super(id);

        this.image = new TrenchioSprite();
        this.container.addChild(this.image);

        this.updateFromData(data, true);
    }

    override updateFromData(data: ObjectsNetData[ObjectCategory.Obstacle], isNew = false): void {
        let texture: string | undefined;

        const full = data.full;
        if (full) {
            const definition = this.definition = full.definition;

            this.position = full.position;
            this.container.position = toPixiCoords(this.position);

            const { rotation, orientation } = full.rotation;
            this.container.rotation = this.rotation = rotation;
            this.orientation = orientation;

            this.layer = full.layer;
            this.updateLayer();

            this.variation = definition.variations !== undefined
                ? (this.id % definition.variations) as Variation
                : undefined;

            if (definition.graphics?.length && !this.graphics) {
                this.graphics = new Graphics();
                this.graphics.zIndex = definition.graphicsZIndex ?? ZIndexes.ObstaclesLayer1;
                for (const graphics of definition.graphics) {
                    this.graphics.beginPath();
                    drawGroundGraphics(graphics.hitbox, this.graphics);
                    this.graphics.closePath();
                    this.graphics.fill(graphics.color);
                }
                this.container.addChild(this.graphics);
            } else if (definition.wall) {
                const { color, borderColor } = definition.wall;
                const dimensions = (definition.hitbox as RectangleHitbox).clone();
                dimensions.scale(PIXI_SCALE);
                const { x, y } = dimensions.min;
                const [w, h] = [dimensions.max.x - x, dimensions.max.y - y];
                const s = WALL_STROKE_WIDTH;
                // (0.98.8: the same wall drawn once and shared by every wall like it: each drew its own copy, and Pixi cut
                // each into triangles again, a building's walls all at once as it came into view)
                const key = `${borderColor}|${color}|${x}|${y}|${w}|${h}|${s}`;
                let context = wallContexts.get(key);
                if (!context) {
                    context = new GraphicsContext()
                        .rect(x, y, w, h)
                        .fill({ color: borderColor })
                        .roundRect(x + s, y + s, w - s * 2, h - s * 2, s)
                        .fill({ color });
                    wallContexts.set(key, context);
                }
                this.graphics = new Graphics(context);
                this.container.addChild(this.graphics);
            }

            if (definition.isTree && definition.frames?.leaves && !this.leavesSprite) {
                this._leavesFrame = `${definition.frames.leaves}${this.variation !== undefined ? `_${(definition.leavesVariations ? this.variation % definition.leavesVariations : this.variation) + 1}` : ""}`;
                this.leavesSprite = new TrenchioSprite().setFrame(this._leavesFrame);

                this.container.addChild(this.leavesSprite);
            }

            if (definition.gunMount && !this.mountSprite) {
                this.mountSprite = new TrenchioSprite()
                    .setFrame(definition.gunMount.weapon)
                    .setScale(1.15)
                    .setPos(0, 10);

                if (definition.gunMount.type === "melee") {
                    this.mountSprite.scale.set(-0.95, -0.95);
                    this.mountSprite
                        .setPos(-12.5, 7)
                        .setRotation(Math.PI / 4);
                }

                this.container.addChild(this.mountSprite);
            }

            if (definition.invisible) this.container.visible = false;

            // If there are multiple particle variations, generate a list of variation image names
            const particleImage = definition.frames?.particle ?? `${definition.idString}_particle`;

            this.particleFrames = definition.particleVariations !== undefined
                ? Array.from({ length: definition.particleVariations }, (_, i) => `${particleImage}_${i + 1}`)
                : [particleImage];

            if ((definition.explosion ?? ("emitParticles" in definition)) && !this.smokeEmitter) {
                this.smokeEmitter = ParticleManager.addEmitter({
                    delay: 400,
                    active: false,
                    spawnOptions: () => ({
                        frames: "smoke_particle",
                        position: this.position,
                        layer: this.layer,
                        zIndex: Numeric.max((definition.zIndex ?? ZIndexes.ObstaclesLayer1) + 1, ZIndexes.Players),
                        lifetime: 3500,
                        scale: { start: 0, end: randomFloat(4, 5) },
                        alpha: { start: 0.9, end: 0 },
                        speed: Vec.fromPolar(randomFloat(-1.9, -2.1), randomFloat(5, 6))
                    })
                });
            }

            if (!this.particleEmitter && "emitParticle" in definition) {
                this.particleEmitter = ParticleManager.addEmitter({
                    delay: 450,
                    active: false,
                    spawnOptions: () => ({
                        frames: definition.emitParticle ?? `${definition.idString}_particle`,
                        position: this.hitbox.randomPoint(),
                        layer: this.layer,
                        zIndex: Numeric.max((definition.zIndex ?? ZIndexes.ObstaclesLayer1) + 1, ZIndexes.Players),
                        lifetime: 2000,
                        rotation: 0,
                        alpha: {
                            start: 1,
                            end: 0
                        },
                        scale: {
                            start: 0.95,
                            end: 1.2
                        },
                        speed: Vec(randomFloat(-1, 1), -5)
                    })
                });
            }

            if (
                definition.sound
                && !this.destroyed
                && !definition.isActivatable
                && !definition.isDoor
            ) {
                if ("names" in definition.sound) definition.sound.names.forEach(name => this.playSound(name, definition.sound));
                else this.playSound(definition.sound.name, definition.sound);
            }

            if (this.activated !== full.activated) {
                this.activated = full.activated;

                if (!isNew && !this.destroyed) {
                    if (definition.isActivatable && definition.sound) {
                        if ("names" in definition.sound) definition.sound.names.forEach(name => this.playSound(name, definition.sound));
                        else this.playSound(definition.sound.name, definition.sound);
                    }

                    if (this.definition.airdrop !== undefined) {
                        const options = (minSpeed: number, maxSpeed: number): Partial<ParticleOptions> => ({
                            zIndex: Numeric.max((this.definition.zIndex ?? ZIndexes.Players) + 1, 4),
                            lifetime: 1000,
                            scale: {
                                start: randomFloat(0.85, 0.95),
                                end: 0,
                                ease: EaseFunctions.quarticIn
                            },
                            alpha: {
                                start: 1,
                                end: 0,
                                ease: EaseFunctions.sexticIn
                            },
                            rotation: { start: randomRotation(), end: randomRotation() },
                            speed: Vec.fromPolar(randomRotation(), randomFloat(minSpeed, maxSpeed))
                        });

                        ParticleManager.spawnParticle({
                            frames: `${this.definition.airdrop.particle}_1`,
                            position: this.position,
                            ...options(8, 18),
                            rotation: { start: 0, end: randomFloat(Math.PI / 2, Math.PI * 2) }
                        } as ParticleOptions);

                        texture = this.definition.airdrop.unlockFrame;

                        if ((this.definition.airdrop.particleVariations ?? 2) > 1) {
                            if (Game.mode.enhancedAirdropParticles) {
                                ParticleManager.spawnParticles(1, () => ({
                                    frames: "airdrop_particle_4",
                                    position: this.hitbox.randomPoint(),
                                    ...options(7, 9)
                                } as ParticleOptions));
                                ParticleManager.spawnParticles(2, () => ({
                                    frames: "airdrop_particle_5",
                                    position: this.hitbox.randomPoint(),
                                    ...options(4, 9)
                                } as ParticleOptions));
                            }

                            this.addTimeout(() => {
                                ParticleManager.spawnParticles(this.definition.airdrop?.particleAmount ?? 2, () => ({
                                    frames: `${this.definition.airdrop?.particle}_2`,
                                    position: this.hitbox.randomPoint(),
                                    ...options(4, 9)
                                } as ParticleOptions));
                                ParticleManager.spawnParticles(this.definition.airdrop?.particleAmount ?? 2, () => ({
                                    frames: `${this.definition.airdrop?.particle}_3`,
                                    position: this.hitbox.randomPoint(),
                                    ...options(4, 9)
                                } as ParticleOptions));
                            }, 800);
                        }
                    }
                }
            }

            this.updateDoor(full, isNew);
        }

        // moved or merged by the Gravity Gloves (definitions/gravity.ts)
        if (data.dynamic) this._updateDynamic(data.dynamic, isNew || full !== undefined);

        const definition = this.definition;

        this.scale = data.scale;

        const destroyScale = definition.scale?.destroy ?? 1;
        const scaleFactor = (this.scale - destroyScale) / ((definition.scale?.spawnMax ?? 1) - destroyScale);

        if (this.smokeEmitter) {
            this.smokeEmitter.active = !this.dead
                && (("emitParticles" in definition && this.activated) || (scaleFactor > 0 && scaleFactor < 0.5));

            if ("emitParticles" in definition) this.smokeEmitter.delay = 300;
            else this.smokeEmitter.delay = Numeric.lerp(150, 3000, scaleFactor);
        }

        if (this.particleEmitter) {
            this.particleEmitter.active = !this.dead && "emitParticle" in definition;
        }

        this.container.scale.set(this.dead ? 1 : this.scale);
        if (definition.idString === BUILD.wall) {
            if (this.dead) this._finishWallBuild(false);
            else {
                this._updateWallBuild(data.building === true);
                this._updateWallDamage(data.damageStage ?? 0, isNew);
            }
        }

        this.powered = data.powered;

        this.waterOverlay = data.waterOverlay;
        if (this.waterOverlay && !this.waterOverlaySprite) {
            const waterOverlaySpriteType: "circle" | "rect" = definition.hitbox.type === HitboxType.Circle ? "circle" : "rect";

            this.waterOverlaySprite = new TrenchioSprite(`water_overlay_${waterOverlaySpriteType}`)
                .setTint(Game.mode.colors.water)
                .setZIndex(ZIndexes.BuildingsFloor);

            if (this.definition.waterOverlay !== undefined) {
                this.waterOverlaySprite.width = this.waterOverlaySprite.width * this.definition.waterOverlay.scaleX;
                this.waterOverlaySprite.height = this.waterOverlaySprite.height * this.definition.waterOverlay.scaleY;
            }

            this.container.addChild(this.waterOverlaySprite);
        }

        if (isNew) {
            if (definition.glow !== undefined) {
                const glow = definition.glow;

                const particle = this._glow ??= ParticleManager.spawnParticle({
                    frames: "_glow_",
                    position: glow.position !== undefined
                        ? Vec.add(this.position, glow.position)
                        : this.position,
                    layer: this.layer,
                    lifetime: Infinity,
                    speed: Vec(0, 0),
                    zIndex: glow.zIndex ?? this.container.zIndex - 0.5,
                    tint: glow.tint,
                    scale: glow.scale
                });

                if (glow.scaleAnim !== undefined) {
                    const { to, duration } = glow.scaleAnim;

                    // offset so that they aren't all synchronized lol
                    window.setTimeout(() => {
                        if (this.dead) return;

                        this._glowTween ??= Game.addTween({
                            target: particle,
                            to: {
                                scale: to
                            },
                            duration,
                            ease: EaseFunctions.cubicOut,
                            yoyo: true,
                            infinite: true
                        });
                    }, Math.random() * 2000);
                }

                const { chance, strength, interval } = glow.flicker ?? { chance: 0, strength: 1, interval: 1 };
                if ((chance ?? 0) > 0) {
                    const This = this;
                    // "i will write bad code but make it look pretty so that it doesn't look like bad code"
                    // -eiπ

                    this._flickerTimeout ??= Game.addTimeout(function flicker(): void {
                        if (particle.dead) return;
                        if (Math.random() < chance) {
                            const old = particle.alpha;
                            particle.alpha *= strength;
                            This._flickerTimeout = Game.addTimeout(() => {
                                if (particle.dead) return;
                                particle.alpha = old;
                                This._flickerTimeout = Game.addTimeout(flicker, interval);
                            }, 50);
                        } else {
                            This._flickerTimeout = Game.addTimeout(flicker, interval);
                        }
                    }, interval);
                }
            }
        }

        // Change the texture of the obstacle and play a sound when it's destroyed
        if (this.dead !== data.dead) {
            this.dead = data.dead;
            // (0.98.7: its bullet holes go with it, however it broke: the residue is whole)
            if (this.dead) DamageMarks.clearHoles(this);

            const showSprites = !this.dead;
            this.leavesSprite?.setVisible(showSprites);
            this.mountSprite?.setVisible(showSprites);
            this.waterOverlaySprite?.setVisible(showSprites);
            if (this.graphics) this.graphics.visible = showSprites;

            // image alpha for trees is changed based on distance to the player
            // this resets it
            if (this.dead) this.image.alpha = 1;

            if (!isNew && !("replaceWith" in definition && definition.replaceWith) && !definition.noDestroyEffect) {
                const playSound = (name: string): void => {
                    this.playSound(name, {
                        falloff: 0.2,
                        maxRange: 96
                    });
                };

                if (data.playMaterialDestroyedSound) {
                    playSound(`${MaterialSounds[definition.material]?.destroyed ?? definition.material}_destroyed`);

                    for (const sound of definition.additionalDestroySounds ?? []) playSound(sound);
                }

                if (definition.noResidue) {
                    this.image.setVisible(false);
                } else {
                    this.image.setFrame(definition.frames?.residue ?? `${definition.idString}_residue`);

                    if (this.waterOverlay) {
                        this.image.setZIndex(ZIndexes.DeadObstacles);
                        this.image.setAlpha(0.5);
                    }

                    if (definition.graphics && !this.image.visible) this.image.visible = true;
                }

                this.container.rotation = this.rotation;
                this.container.scale.set(this.scale);

                if (this.smokeEmitter) {
                    this.smokeEmitter.active = false;
                    this.smokeEmitter.destroy();
                }

                if (this.particleEmitter) {
                    this.particleEmitter.active = false;
                    this.particleEmitter.destroy();
                }

                ParticleManager.spawnParticles(10, () => ({
                    frames: this.particleFrames,
                    position: this.hitbox.randomPoint(),
                    layer: this.layer,
                    zIndex: (definition.zIndex ?? ZIndexes.ObstaclesLayer1) + 1,
                    lifetime: 1500,
                    rotation: {
                        start: randomRotation(),
                        end: randomRotation()
                    },
                    scale: {
                        start: randomFloat(0.85, 0.95),
                        end: 0,
                        ease: EaseFunctions.quarticIn
                    },
                    alpha: {
                        start: 1,
                        end: 0,
                        ease: EaseFunctions.sexticIn
                    },
                    speed: Vec.fromPolar(randomRotation(), randomFloat(4, 9) * (definition.explosion ? 3 : 1))
                }));
            }

            this._glowTween?.kill();
            this._flickerTimeout?.kill();
            this._glow?.kill();
        }

        // (0.98.7: made again as it comes back on screen, standing: its bullet holes drawn on it as they were)
        if (isNew) DamageMarks.restoreHoles(this);

        this.container.zIndex = this.dead
            ? ZIndexes.DeadObstacles
            : this.definition.zIndex ?? ZIndexes.ObstaclesLayer1;

        // a street light's lamp (0.98.1): lit at night while it stands, dark once it's shot down
        const lamp = definition.lamp;
        if (lamp) {
            const at = Vec.addAdjust(this.position, lamp.offset, this.orientation);
            NightLight.setLamp(this, this.dead ? undefined : { x: at.x, y: at.y, layer: this.layer, radius: lamp.radius });
        }

        if (this._door === undefined) {
            // a built wall stands at any angle: its hitbox is made for that angle, exactly as on the server
            this.hitbox = definition.idString === BUILD.wall
                ? wallHitbox(this.position, this.rotation)
                : definition.hitbox.transform(this.position, this.scale, this.orientation);
        }

        if (
            definition.invisible
            || (this.dead && definition.noResidue)
            || (!this.dead && (definition.graphics || definition.wall))
        ) {
            this.image.setVisible(false);
            return;
        }

        if (!texture) {
            // TODO opened, powered, and activated states can probably be combined in some way
            if (this.dead) {
                texture = definition.frames?.residue ?? `${definition.idString}_residue`;
            } else if (!this.door?.locked && this.powered && definition.frames?.opened && !(definition as { openOnce?: boolean }).openOnce) {
                texture = definition.frames.opened;
            } else if (!this.door?.locked && !this.powered && this.door?.offset !== 0 && definition.frames?.opened) {
                texture = definition.frames.opened;
            } else if (!this.door?.locked && !this.powered && definition.requiresPower && definition.frames?.powered) {
                texture = definition.frames.powered;
            } else if (this.activated && definition.frames?.activated) {
                texture = definition.frames?.activated;
            } else {
                texture = definition.frames?.base ?? definition.idString;
            }
        }

        if (this.variation !== undefined && !this.dead && !(definition.isTree && !definition.trunkVariations)) {
            // biome-ignore lint/style/noNonNullAssertion: idk man I'm tired
            texture += `_${definition.isTree && definition.leavesVariations ? Math.ceil((this.variation + 1) / definition.trunkVariations!) : this.variation + 1}`;
        }

        this.image.setVisible(true).setFrame(texture);
        this._snow(texture);

        if (this.waterOverlay && this.dead) {
            this.image.setZIndex(ZIndexes.DeadObstacles);
            this.image.setAlpha(0.5);
        }

        if (definition.tint !== undefined) {
            this.image.setTint(definition.tint);
            this.leavesSprite?.setTint(definition.tint);
        }
    }

    /**
     * its cap of snow (0.98.5, the snow sky): on its top, or a tree's crown, while it stands outdoors; none under a
     * roof, on a door, a window or a stair, or on anything lying flat on the ground
     */
    private _snow(frame: string): void {
        const definition = this.definition;
        const crown = this.leavesSprite;
        if (!SnowLook.active) return;
        const flat = (definition.zIndex ?? ZIndexes.ObstaclesLayer1) < ZIndexes.ObstaclesLayer1;
        // (nor on a picture drawn in a colour of its own, a tent's wall: its cap would take the colour)
        let bare = this.dead || flat || definition.isDoor || definition.isWindow || definition.isStair || definition.invisible || definition.tint !== undefined;
        // (under a roof last: the buildings' ceilings are gone through only for a thing that could carry snow)
        if (!bare) bare = this._indoors ??= underRoof(this.position, this.layer);
        if (crown) {
            if (bare || !this._leavesFrame) SnowLook.uncap(crown);
            else SnowLook.cap(crown, this._leavesFrame, "thing");
            SnowLook.uncap(this.image);
            return;
        }
        if (bare) SnowLook.uncap(this.image);
        else SnowLook.cap(this.image, frame, "thing");
    }

    override updateDebugGraphics(): void {
        if (!DEBUG_CLIENT) return;

        const definition = this.definition;
        const alpha = Game.activePlayer !== undefined && equivLayer(this, Game.activePlayer) ? 1 : DIFF_LAYER_HITBOX_OPACITY;

        if (definition.isStair && GameConsole.getBuiltInCVar("db_show_hitboxes_stairs")) {
            const hitbox = this.hitbox as RectangleHitbox;

            const min = hitbox.min;
            const max = hitbox.max;

            // using the same numbering system as server-side, but with array indexes
            const sides = [
                [
                    Vec(min.x, min.y),
                    Vec(max.x, min.y)
                ],
                [
                    Vec(max.x, min.y),
                    Vec(max.x, max.y)
                ],
                [
                    Vec(max.x, max.y),
                    Vec(min.x, max.y)
                ],
                [
                    Vec(min.x, max.y),
                    Vec(min.x, min.y)
                ]
            ];

            const { high: highDef, low: lowDef } = definition.activeEdges;
            const [high, low] = [
                Numeric.absMod(highDef - this.orientation, 4),
                Numeric.absMod(lowDef - this.orientation, 4)
            ];

            if (Math.abs(high - low) === 1) {
                for (let i = 0; i < 4; i++) {
                    let color: 0xff0000 | 0x00ff00 = 0xff0000;
                    switch (true) {
                        case i === high: { // active edge
                            color = 0xff0000;
                            break;
                        }
                        case i === low: { // active edge
                            color = 0x00ff00;
                            break;
                        }
                        case Math.abs(i - low) === 2: { // opposite of low edge -> high edge
                            color = 0xff0000;
                            break;
                        }
                        case Math.abs(i - high) === 2: { // opposite of high edge -> low edge
                            color = 0x00ff00;
                            break;
                        }
                    }

                    DebugRenderer.addLine(sides[i][0], sides[i][1], color, alpha);
                }

                // determine the line's endpoints
                const [vertexA, vertexB] = high + low === 3
                    ? [min, max]
                    : [
                        { x: max.x, y: min.y },
                        { x: min.x, y: max.y }
                    ];
                const ratio = (vertexB.y - vertexA.y) / (vertexB.x - vertexA.x);
                const protrusion = Numeric.min(2.5, 2.5 / ratio);

                DebugRenderer.addLine(
                    Vec(vertexA.x - protrusion, vertexA.y - protrusion * ratio),
                    vertexA,
                    0xffff00,
                    alpha
                ).addLine(
                    vertexB,
                    Vec(vertexB.x + protrusion, vertexB.y + protrusion * ratio),
                    0xffff00,
                    alpha
                ).addLine(
                    vertexA,
                    vertexB,
                    0xffff00,
                    0.25 * alpha
                );
            } else {
                DebugRenderer.addHitbox(hitbox,
                    definition.noCollisions || this.dead
                        ? HITBOX_COLORS.obstacleNoCollision
                        : HITBOX_COLORS.stair,
                    alpha
                );

                DebugRenderer.addLine(
                    sides[high][0],
                    sides[high][1],
                    0xff0000
                );
                DebugRenderer.addLine(
                    sides[low][0],
                    sides[low][1],
                    0x00ff00
                );
            }
        } else if (GameConsole.getBuiltInCVar("db_show_hitboxes_obstacles")) {
            DebugRenderer.addHitbox(this.hitbox,
                definition.noCollisions || this.dead
                    ? HITBOX_COLORS.obstacleNoCollision
                    : HITBOX_COLORS.obstacle,
                alpha
            );
        }

        if (!GameConsole.getBuiltInCVar("db_show_hitboxes_obstacles")) return;

        if (definition.isDoor && definition.operationStyle !== "slide") {
            DebugRenderer.addCircle(
                0.2,
                Vec.addAdjust(this.position, definition.hingeOffset, this.orientation),
                HITBOX_COLORS.obstacleNoCollision,
                alpha
            );
        }

        if (definition.spawnHitbox) {
            DebugRenderer.addHitbox(
                definition.spawnHitbox.transform(this.position, 1, this.orientation),
                HITBOX_COLORS.spawnHitbox,
                alpha
            );
        }
    }

    override update(): void {
        // (nothing of its own each frame: its shadow is managers/shadowLook.ts's, drawn from its picture as it stands)
    }

    override updateInterpolation(): void {
        if (!this.dynamic) return;
        this.updateContainerPosition();
        // tumbling, smoothed between updates like the position
        const t = Numeric.min((Date.now() - this._spinAt) / Game.serverDt, 1);
        this.container.rotation = this.rotation + this._spinFrom + Angle.minimize(this._spinFrom, this.spin) * t;
    }

    /** moved by the Gravity Gloves: it follows the server's position (smoothed), tumbles, and may belong to a group */
    dynamic = false;
    /** the merged group it belongs to (0: none); the blue liquid is drawn by GravityFx */
    bond = 0;
    /** how far it has tumbled from its usual angle (radians) */
    spin = 0;
    private _spinFrom = 0;
    private _spinAt = 0;
    private _motion?: { at: number, velocity: Vector };

    /** how fast the Gravity Gloves have it moving (units per ms, from its last two updates) */
    get speed(): number {
        const motion = this._motion;
        return motion && Date.now() - motion.at < 150 ? Vec.len(motion.velocity) : 0;
    }

    private _updateDynamic(dynamic: NonNullable<ObjectsNetData[ObjectCategory.Obstacle]["dynamic"]>, jump: boolean): void {
        this.dynamic = true;
        const now = Date.now();
        if (!Vec.equals(dynamic.position, this.position)) {
            // a sudden change of speed while flying free (not held) is a hit: a thud and some splinters where it struck
            const motion = this._motion;
            if (!jump && motion) {
                const elapsed = Math.max(8, now - motion.at);
                const velocity = Vec.scale(Vec.sub(dynamic.position, this.position), 1 / elapsed);
                const change = Vec.len(Vec.sub(velocity, motion.velocity));
                if (change > 0.035 && Vec.len(motion.velocity) > 0.04 && !GravityFx.isHeld(this)) {
                    const angle = Math.atan2(motion.velocity.y, motion.velocity.x);
                    this.hitEffect(Vec.add(this.position, Vec.fromPolar(angle, 2)), angle + Math.PI);
                }
                this._motion = { at: now, velocity };
            } else {
                this._motion = { at: now, velocity: Vec(0, 0) };
            }
            this.position = dynamic.position;
            if (jump || !GameConsole.getBuiltInCVar("cv_movement_smoothing")) this.container.position = toPixiCoords(this.position);
        } else if (this._motion && now - this._motion.at > 120) {
            this._motion = { at: now, velocity: Vec(0, 0) };
        }

        if (dynamic.spin !== this.spin || jump) {
            this._spinFrom = jump ? dynamic.spin : this._shownSpin();
            this._spinAt = now;
            this.spin = dynamic.spin;
            if (jump || !GameConsole.getBuiltInCVar("cv_movement_smoothing")) {
                this._spinFrom = this.spin;
                this.container.rotation = this.rotation + this.spin;
            }
        }

        if (dynamic.bond !== this.bond) {
            const was = this.bond;
            this.bond = dynamic.bond;
            GravityFx.setBond(this, was, this.bond, jump);
        }
    }

    private _shownSpin(): number {
        const t = Numeric.min((Date.now() - this._spinAt) / Game.serverDt, 1);
        return this._spinFrom + Angle.minimize(this._spinFrom, this.spin) * t;
    }

    updateDoor(data: ObjectsNetData[ObjectCategory.Obstacle]["full"], isNew = false): void {
        if (!data?.door || !data.definition.isDoor) return;
        const definition = data.definition;

        if (!this._door) this._door = { offset: 0 };

        this.rotation = Angle.orientationToRotation(this.orientation);

        const hitboxes = calculateDoorHitboxes(definition, this.position, this.orientation);

        this._door.openHitbox = hitboxes.openHitbox;
        if ("openAltHitbox" in hitboxes) this._door.openAltHitbox = hitboxes.openAltHitbox;

        this._door.locked = data.door.locked;

        let backupHitbox = (definition.hitbox as RectangleHitbox).transform(this.position, this.scale, this.orientation);

        this._door.closedHitbox = backupHitbox.clone();

        switch (data.door.offset) {
            case 1: {
                backupHitbox = this._door.openHitbox.clone();
                break;
            }
            case 3: {
                // biome-ignore lint/style/noNonNullAssertion: offset 3 means that this is a "swivel" door, meaning that there is an altHitbox
                backupHitbox = this._door.openAltHitbox!.clone();
                break;
            }
        }

        const savedBackupHitbox = backupHitbox;
        if (definition.interactionDelay && !this.powered && this.door?.locked) {
            backupHitbox = this._door.closedHitbox.clone();
        }

        this.hitbox = this._door.hitbox = backupHitbox;

        const offset = data.door.offset;
        switch (definition.operationStyle) {
            case "slide":
                if (isNew) {
                    const x = offset
                        ? (definition.slideFactor ?? 1) * (
                            this.orientation & 1
                                ? backupHitbox.min.y - backupHitbox.max.y
                                : backupHitbox.min.x - backupHitbox.max.x
                        ) * PIXI_SCALE
                        : 0;
                    this.image.setPos(x, 0);
                }
                break;
            case "swivel":
            default:
                if (isNew) this.image.setRotation(Angle.orientationToRotation(offset));
                this.image.anchor.set(0, 0.5);
                this.image.setPos(
                    definition.hingeOffset.x * PIXI_SCALE,
                    definition.hingeOffset.y * PIXI_SCALE
                );
                break;
        }

        if (isNew) {
            this._door.offset = offset;

            if (this.definition.isDoor && this.definition.hideWhenOpen) {
                this.doorMask = new Graphics();
                this.doorMask.alpha = 0;
                this.container.addChild(this.doorMask);

                const { min, max } = this.definition.hitbox;
                this.doorMask
                    .beginPath()
                    .rect(
                        min.x * PIXI_SCALE,
                        min.y * PIXI_SCALE,
                        (max.x - min.x) * PIXI_SCALE,
                        (max.y - min.y) * PIXI_SCALE
                    )
                    .closePath()
                    .fill(0xffffff);
                this.image.mask = this.doorMask;
            }
        } else if (offset !== this._door.offset) {
            this._door.offset = offset;

            const soundName = definition.doorSound ?? "door";
            this.playSound(
                `${soundName}_${offset ? "open" : "close"}`,
                {
                    falloff: 0.3,
                    maxRange: 48
                }
            );

            if (definition.operationStyle !== "slide") {
                Game.addTimeout(() => {
                    Game.addTween({
                        target: this.image,
                        to: { rotation: Angle.orientationToRotation(offset) },
                        duration: definition.animationDuration ?? 150
                    });
                    if (this._door) this.hitbox = this._door.hitbox = savedBackupHitbox;
                }, definition.interactionDelay ?? 0);
            } else {
                const x = offset
                    ? (definition.slideFactor ?? 1) * (
                        this.orientation & 1
                            ? backupHitbox.min.y - backupHitbox.max.y
                            : backupHitbox.min.x - backupHitbox.max.x
                    ) * PIXI_SCALE
                    : 0;

                Game.addTween({
                    target: this.image.position,
                    to: { x, y: 0 },
                    duration: definition.animationDuration ?? 150
                });
            }
        }
    }

    canInteract(player: Player): boolean {
        interface DoorDef { openOnce?: boolean, automatic?: boolean }
        if (this._door !== undefined
            && (this.definition as DoorDef).openOnce
            && !this._door.locked
            && !((this.definition as DoorDef).openOnce && this._door.offset === 0)) return false;
        return !this.dead && !this.definition?.damage
            && (
                this.definition.interactOnlyFromSide === undefined
                || this.definition.interactOnlyFromSide === (this.hitbox as RectangleHitbox).getSide(player.position)
            )
            && (
                (
                    this._door !== undefined
                    && !this._door.locked
                    && !(this.definition as DoorDef).automatic
                    && !((this.definition as DoorDef).openOnce && this._door.offset === 1)
                ) || (
                    this.definition.isActivatable === true
                    && (this.definition.requiredItem === undefined || player.activeItem.idString === this.definition.requiredItem)
                    && !this.activated
                )
                // (1.08: a roof's hatch, a ladder's top or foot, a roof's stair door: F takes whoever's by it through, as
                // on the server; not while they're on it already: 1.09)
                || (this.definition.passage !== undefined && !player.climbing && !player.downed)
            );
    }

    /** something struck it (`angle`: the surface's outward angle there); `strike`: it was a bullet */
    hitEffect(position: Vector, angle: number, strike?: BulletStrike): void {
        if (this.definition.noHitEffect) return;
        if (this.definition.idString === BUILD.wall && !this.dead) this._shakeWall();

        const { material } = this.definition;
        // a bullet's own impact on what it's made of (managers/combatSounds.ts), and what flies off it (combatFx.ts)
        const kind = strike ? impactKindOf(material, this.definition.idString) : undefined;
        if (kind !== undefined && strike) {
            CombatFx.impact(position, angle, kind, this.layer, strike.direction, strike.damage, particleTint(this.particleFrames));
            // (0.98.7: the bullet's hole, kept on it for the match)
            DamageMarks.hole(this, position, angle, kind, strike.damage);
        }
        if (kind === undefined || !strike || !CombatSounds.impact(kind, this.id, position, this.layer, strike.glanced)) {
            if (!this.definition.hitSoundVariations) this.hitSound?.stop();

            this.hitSound = SoundManager.play(
                `${MaterialSounds[material]?.hit ?? material}_hit_${this.definition.hitSoundVariations ? random(1, this.definition.hitSoundVariations) : randomBoolean() ? "1" : "2"}`,
                {
                    position,
                    falloff: 0.2,
                    maxRange: 96,
                    layer: this.layer
                }
            );
        }

        ParticleManager.spawnParticle({
            frames: this.particleFrames,
            position,
            zIndex: Numeric.max((this.definition.zIndex ?? ZIndexes.Players) + 1, 4),
            lifetime: 600,
            layer: this.layer,
            scale: { start: 0.9, end: 0.2 },
            alpha: { start: 1, end: 0.65 },
            speed: Vec.fromPolar((angle + randomFloat(-0.3, 0.3)), randomFloat(2.5, 4.5))
        });
    }

    /** a wall being built (B): a blueprint of the whole wall, the planks filling in from one end, then a thunk */
    private _wallBuild?: { readonly startedAt: number, readonly blueprint: Graphics, readonly mask: Graphics, lastHammer: number, lastDust: number, raf: number, serverDone: boolean };

    private _updateWallBuild(building: boolean): void {
        if (building && !this._wallBuild) {
            const length = BUILD.length * PIXI_SCALE, thickness = BUILD.thickness * PIXI_SCALE;
            const blueprint = new Graphics();
            blueprint.rect(-length / 2, -thickness / 2, length, thickness).fill({ color: 0x5ec8ff, alpha: 0.22 });
            for (let k = 1; k < 8; k++) {
                const x = -length / 2 + length * k / 8;
                blueprint.moveTo(x, -thickness / 2).lineTo(x, thickness / 2);
            }
            blueprint.stroke({ width: 2, color: 0x9fdcff, alpha: 0.55 });
            blueprint.rect(-length / 2, -thickness / 2, length, thickness).stroke({ width: 4, color: 0x7fd4ff, alpha: 0.95 });
            const mask = new Graphics();
            this.container.addChildAt(blueprint, 0);
            this.container.addChild(mask);
            this.image.mask = mask;
            const state = { startedAt: performance.now(), blueprint, mask, lastHammer: 0, lastDust: 0, raf: 0, serverDone: false };
            this._wallBuild = state;
            const frame = (): void => {
                if (this._wallBuild !== state) return;
                const now = performance.now();
                const progress = Math.min(1, (now - state.startedAt) / BUILD.durationMs);
                // the planks fill in from one end of the wall to the other
                state.mask.clear().rect(-length / 2, -thickness / 2 - 8, length * progress, thickness + 16).fill({ color: 0xffffff });
                if (progress < 1 && now - state.lastHammer > 260) {
                    state.lastHammer = now;
                    SoundManager.play(`crate_hit_${Math.random() < 0.5 ? 1 : 2}`, { position: this.position, falloff: 0.2, maxRange: 96, layer: this.layer });
                }
                if (progress < 1 && now - state.lastDust > 110) {
                    state.lastDust = now;
                    this._wallDust(-length / 2 + length * progress, 1, 0.006);
                }
                if (progress >= 1 && state.serverDone) {
                    this._finishWallBuild(true);
                    return;
                }
                state.raf = requestAnimationFrame(frame);
            };
            state.raf = requestAnimationFrame(frame);
        } else if (!building && this._wallBuild) {
            // the server says it's done; the animation finishes filling in, then the thunk
            this._wallBuild.serverDone = true;
        }
    }

    /** a hit wall shakes (and the hit's splinters fly); walls keep their size, so this is the "it's taking damage" cue */
    private _shakeUntil = 0;

    /** the wall's tint: darker the weaker it is */
    private _wallTint(): number {
        return [0xffffff, 0xe2c7a2, 0xc09a6e, 0x94704c][this._damageStage] ?? 0xffffff;
    }

    private _shakeWall(): void {
        // a brief reddish flash with the shake: the hit landed
        this.image.tint = 0xff9f8a;
        window.setTimeout(() => { if (!this.destroyed && !this.dead) this.image.tint = this._wallTint(); }, 90);
        const now = performance.now();
        const running = this._shakeUntil > now;
        this._shakeUntil = now + 180;
        if (running) return;
        const step = (): void => {
            const at = performance.now();
            if (this.destroyed || this.dead || at >= this._shakeUntil) {
                this.container.pivot.set(0, 0);
                return;
            }
            const k = (this._shakeUntil - at) / 180;
            this.container.pivot.set((Math.random() - 0.5) * 5 * k, (Math.random() - 0.5) * 5 * k);
            requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }

    /** cracks that spread as a wall weakens (0 intact, 1 cracked, 2 badly cracked, 3 about to break) */
    private _cracks?: Graphics;
    private _damageStage = 0;

    private _updateWallDamage(stage: number, isNew: boolean): void {
        if (stage === this._damageStage && (stage === 0 || this._cracks)) return;
        const previous = this._damageStage;
        this._damageStage = stage;
        this._cracks?.destroy();
        this._cracks = undefined;
        this.image.tint = this._wallTint();
        if (stage > 0) {
            const length = BUILD.length * PIXI_SCALE, thickness = BUILD.thickness * PIXI_SCALE;
            // the same cracks every time for the same wall (seeded by its id)
            let seed = (this.id * 2654435761) >>> 0;
            const random = (): number => {
                seed = (seed + 0x6D2B79F5) >>> 0;
                let t = seed;
                t = Math.imul(t ^ (t >>> 15), t | 1);
                t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
                return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
            };
            const cracks = new Graphics();
            cracks.context.batchMode = "batch"; // in the wall's own batch, not a draw call of its own
            // cracks running along the planks (long enough to read at a glance), more and heavier as it weakens
            const count = [0, 3, 5, 8][stage] ?? 8;
            for (let k = 0; k < count; k++) {
                let x = -length / 2 + length * (0.06 + 0.7 * random());
                let y = -thickness / 2 + thickness * (0.2 + 0.6 * random());
                cracks.moveTo(x, y);
                const run = length * (0.12 + 0.1 * stage + 0.08 * random());
                const segments = 4 + Math.floor(random() * 3);
                for (let s = 1; s <= segments; s++) {
                    x = Math.min(length / 2 - 16, x + run / segments); // stays inside the planks, short of the posts
                    y = Math.max(-thickness / 2 + 3, Math.min(thickness / 2 - 3, y + (random() - 0.5) * thickness * 0.7));
                    cracks.lineTo(x, y);
                }
            }
            cracks.stroke({ width: [0, 3, 3.5, 4][stage] ?? 4, color: 0x1a0f07, alpha: 1 });
            if (stage >= 2) {
                // chunks knocked out of the planks
                for (let k = 0; k < stage * 2; k++) {
                    const x = -length / 2 + length * (0.1 + 0.8 * random()), top = random() < 0.5;
                    cracks.rect(x, top ? -thickness / 2 : thickness / 2 - 6, 6 + random() * 8, 6).fill({ color: 0x24160b, alpha: 0.6 });
                }
            }
            if (stage === 3) {
                // split along the wall: it's about to give
                cracks.moveTo(-length / 2 + 14, 1).lineTo(length / 2 - 14, -1).stroke({ width: 2, color: 0x24160b, alpha: 0.8 });
            }
            this.container.addChild(cracks);
            this._cracks = cracks;
        }
        if (!isNew && stage > previous) {
            // a new crack: splinters fly and the wood creaks
            const length = BUILD.length * PIXI_SCALE;
            for (let k = 0; k < 4; k++) this._wallDust(-length / 2 + length * (0.15 + 0.7 * Math.random()), 1, 0.012);
            SoundManager.play("crate_hit_1", { position: this.position, falloff: 0.2, maxRange: 96, layer: this.layer });
        }
    }

    /** wood dust at a point along the wall (pixels from its centre, along its length) */
    private _wallDust(alongPixels: number, count: number, speed: number): void {
        const at = Vec.add(this.position, Vec.fromPolar(this.container.rotation, alongPixels / PIXI_SCALE));
        for (let k = 0; k < count; k++) {
            const angle = Math.random() * Math.PI * 2;
            ParticleManager.spawnParticle({
                frames: "crate_particle",
                position: at,
                layer: this.layer,
                zIndex: this.container.zIndex + 1,
                lifetime: 380,
                scale: { start: 0.55, end: 0.15 },
                alpha: { start: 0.9, end: 0 },
                speed: Vec.fromPolar(angle, speed),
                rotation: { start: angle, end: angle + 2 }
            });
        }
    }

    private _finishWallBuild(effects: boolean): void {
        const state = this._wallBuild;
        if (!state) return;
        this._wallBuild = undefined;
        cancelAnimationFrame(state.raf);
        this.image.mask = null;
        state.mask.destroy();
        state.blueprint.destroy();
        if (!effects || this.dead) return;
        // done: a thunk, a burst of wood dust along the wall, and a small pop
        SoundManager.play("crate_hit_2", { position: this.position, falloff: 0.2, maxRange: 128, layer: this.layer });
        const length = BUILD.length * PIXI_SCALE;
        for (let k = 0; k <= 6; k++) this._wallDust(-length / 2 + length * k / 6, 1, 0.01);
        const started = performance.now();
        const pop = (): void => {
            if (this.destroyed || this.dead) return;
            const k = Math.min(1, (performance.now() - started) / 170);
            this.container.scale.set(1 + 0.07 * Math.sin(k * Math.PI));
            if (k < 1) requestAnimationFrame(pop);
        };
        requestAnimationFrame(pop);
    }

    override destroy(): void {
        // (0.98.7: let go off screen, its holes are kept for when it comes back; broken, they went with it)
        DamageMarks.stashHoles(this);
        // (its lamp gone with it: 0.98.1)
        if (this.definition.lamp) NightLight.setLamp(this, undefined);
        if (this.bond) GravityFx.setBond(this, this.bond, 0, true);
        this._finishWallBuild(false);
        this._cracks?.destroy();
        this._cracks = undefined;
        super.destroy();
        this.graphics?.destroy();
        this.image.destroy();
        this.mountSprite?.destroy();
        this.waterOverlaySprite?.destroy();
        this.doorMask?.destroy();
        this.smokeEmitter?.destroy();
        this.particleEmitter?.destroy();
        this._glow?.kill();
        this._glowTween?.kill();
        this._flickerTimeout?.kill();
    }
}
